import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link as RouterLink } from 'react-router-dom'
import { Link } from '@oasisprotocol/ui-library/src/components/link'
import { SearchScope } from '../../../types/searchScope'
import { AbiPlaygroundLink } from './AbiPlaygroundLink'
import { Badge } from '@oasisprotocol/ui-library/src/components/badge'
import { RouteUtils } from '../../utils/route-utils'
import { checkSourcifyVerification } from '../../utils/sourcify'

export const verificationIconBoxHeight = 28

export const VerificationIcon: FC<{
  address_eth: string
  scope: SearchScope
  verificationLevel?: 'full' | 'partial'
  hideLink?: boolean
}> = ({ address_eth, scope, verificationLevel, hideLink }) => {
  const { t } = useTranslation()
  const [sourcifyLevel, setSourcifyLevel] = useState<'full' | 'partial' | undefined>()

  useEffect(() => {
    if (verificationLevel || !address_eth) return
    let cancelled = false
    checkSourcifyVerification(address_eth)
      .then(level => {
        if (!cancelled) setSourcifyLevel(level)
      })
      .catch(() => {
        if (!cancelled) setSourcifyLevel(undefined)
      })
    return () => {
      cancelled = true
    }
  }, [address_eth, verificationLevel])

  const effectiveLevel = verificationLevel ?? sourcifyLevel
  const label =
    effectiveLevel === 'full'
      ? t('contract.verification.isVerified')
      : effectiveLevel === 'partial'
        ? t('contract.verification.isPartiallyVerified')
        : t('contract.verification.isNotVerified')
  const statusVariant =
    effectiveLevel === 'full' ? 'success' : effectiveLevel === 'partial' ? 'partial-success' : 'error'

  const verifyHref = RouteUtils.getVerifyContractRoute(scope, address_eth)

  return (
    <>
      <Badge variant={statusVariant}>{label}</Badge>
      {!hideLink &&
        (effectiveLevel ? (
          <span className="text-xs">
            &nbsp; &nbsp;
            <Link asChild className="text-inherit underline">
              <RouterLink to={`${RouteUtils.getAccountRoute(scope, address_eth)}/code`}>
                {t('contract.verification.viewCode')}
              </RouterLink>
            </Link>
            <AbiPlaygroundLink address_eth={address_eth} scope={scope} />
          </span>
        ) : (
          <span className="text-xs">
            &nbsp; &nbsp;
            <Link asChild className="text-inherit underline font-semibold">
              <RouterLink to={verifyHref}>{t('contract.verification.verifyInSourcify')}</RouterLink>
            </Link>
          </span>
        ))}
    </>
  )
}
